import { Component, TemplateRef, computed, input, output, viewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  EHeaderClasses,
  ICustomColumn,
  IDatatableConfiguration,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { GroupedDatatableComponent } from '@shared/components/grouped-datatable/grouped-datatable.component';
import {
  IGroupedDatatableColumn,
  IGroupedDatatableConfiguration,
  IGroupedDatatableCustomColumn,
} from '@shared/components/grouped-datatable/grouped-datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { IGroupedSkeletonLoaderConfig } from '@shared/components/skeleton-loader/grouped-skeleton-loader.interface';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { ECampaignDetailsTab } from '../../campaign-details.interface';
import { ICampaignExportConfig } from '../../campaign-export-config/campaign-export-config.interface';
import {
  ICampaignDetailsDeliverablesGroupedChildRow,
  ICampaignDetailsDeliverablesGroupedParentRow,
  ICampaignDetailsDeliverablesTableRow,
  TCampaignDetailsDeliverablesGroupedRow,
} from '../campaign-details-deliverables.interface';

@Component({
  imports: [
    DatatableComponent,
    GroupedDatatableComponent,
    IconBadgeComponent,
    StatusBadgeComponent,
    MatTooltipModule,
    TextClampTooltipDirective,
    IconComponent,
    SocialBadgeComponent,
  ],
  selector: 'app-campaign-details-deliverables-table',
  styleUrl: './campaign-details-deliverables-table.component.scss',
  templateUrl: './campaign-details-deliverables-table.component.html',
})
export class CampaignDetailsDeliverablesTableComponent {
  protected readonly currentTab = toEventFrontendTab(ECampaignDetailsTab.deliverables);

  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();

  /**
   * Which entity the swappable column shows: 'creator' (campaign-details' own usage, default -
   * one campaign, many creators) or 'campaign' (creator-details' Deliverables tab - one creator,
   * many campaigns). This SWAPS the column (id, label, data source, template), it never adds a
   * second one alongside it.
   */
  public readonly contextColumn = input<'creator' | 'campaign'>('creator');

  /**
   * Namespaces the shared table/grouped-table's localStorage `storageKey` (see
   * TableStateService) so campaign-details' and creator-details' Deliverables tabs don't leak
   * sort/pagination/expand state into each other - they're two different persisted tables that
   * happen to share this component.
   */
  public readonly storageKeyPrefix = input<string>('campaign-details');

  public readonly groupedMode = input<boolean>(false);
  public readonly groupedRowData = input<TCampaignDetailsDeliverablesGroupedRow[]>([]);
  public readonly loading = input<boolean>(false);
  public readonly rowData = input<ICampaignDetailsDeliverablesTableRow[]>([]);

  public readonly groupedRowGroupClicked = output<ICampaignDetailsDeliverablesGroupedParentRow>();
  public readonly rowClicked = output<ICampaignDetailsDeliverablesTableRow>();
  /**
   * Fired when the Campaign column's icon/name is clicked (creator-details' multi-campaign
   * Deliverables tab only - see contextColumn's own doc-comment; the column is never rendered in
   * campaign-details' own 'creator' context, so this never fires there). Row is whichever shape
   * the click landed on (grouped parent or plain row) - both carry campaignId/creatorId.
   */
  public readonly campaignClicked = output<
    ICampaignDetailsDeliverablesGroupedParentRow | ICampaignDetailsDeliverablesTableRow
  >();

  public readonly groupedSkeletonLoaderConfig = computed<IGroupedSkeletonLoaderConfig>(() => ({
    childColumns: [
      {
        borderRadius: '4px',
        circleSize: '14px',
        className: 'min-w-0',
        type: 'icon',
      },
      {
        borderRadius: '100vw',
        className: 'min-w-0',
        columnPadding: '8px 0 8px 16px',
        height: '24px',
        textWidth: '90px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        className: 'min-w-0',
        height: '20px',
        textWidth: '120px',
        type: 'text',
      },
    ],
    childIndentPx: 0,
    childRowGridTemplateColumns: '40px 132px minmax(0,1fr)',
    childRowsPerGroup: 2,
    childWrapperClass: 'box-border min-w-0',
    childWrapperStyle: {
      marginLeft: '18%',
      width: 'calc(82% - 8px)',
    },
    detailMode: 'expanded',
    expandColumn: { className: 'hidden' },
    groupCount: 5,
    headerMargin: '0',
    headerPadding: '0 16px',
    parentColumns: [
      // Both the creator and campaign columns show an icon badge (creator.icon / campaign.iconId
      // are both real file references app-icon-badge resolves) - same placeholder either way.
      {
        borderRadius: '4px',
        circleSize: '24px',
        className: 'min-w-0',
        hasText: true,
        iconRadius: '100px',
        textWidth: '96px',
        type: 'icon',
      },
      { borderRadius: '4px', className: 'min-w-0', height: '20px', textWidth: '120px', type: 'text' },
      { borderRadius: '100vw', className: 'min-w-0', height: '20px', textWidth: '100px', type: 'text' },
      { borderRadius: '100vw', className: 'min-w-0', height: '20px', textWidth: '65px', type: 'text' },
      { borderRadius: '4px', className: 'min-w-0', height: '20px', textWidth: '40px', type: 'text' },
    ],
    parentGridTemplateColumns: 'minmax(0,18%) minmax(0,20%) minmax(0,16%) minmax(0,14%) minmax(0,10%) 40px',
    rowSeparatorEnabled: true,
    showColumns: true,
    showExpandAllTogglePlaceholder: true,
    showPaginator: true,
    showSearchField: false,
  }));

  public readonly skeletonLoaderConfig = computed<ISkeletonLoaderConfig>(() => ({
    columns: [
      {
        borderRadius: '100vw',
        className: 'w-[12.5%]',
        height: '24px',
        rowPadding: '16px 0',
        textWidth: '91px',
        type: 'text',
      },
      { borderRadius: '4px', className: 'w-[15%]', height: '20px', textWidth: '120px', type: 'text' },
      { borderRadius: '100vw', className: 'w-[12.5%]', height: '20px', textWidth: '65px', type: 'text' },
      { borderRadius: '4px', className: 'w-[20%]', height: '20px', textWidth: '120px', type: 'text' },
      // Same "both columns show an icon badge" rationale as groupedSkeletonLoaderConfig.parentColumns above.
      {
        borderRadius: '4px',
        circleSize: '24px',
        className: 'w-[25%]',
        hasText: true,
        iconRadius: '100px',
        textWidth: '96px',
        type: 'icon',
      },
      {
        borderRadius: '4px',
        circleSize: '16px',
        className: 'w-[15%]',
        hasText: true,
        textWidth: '120px',
        type: 'icon',
      },
    ],
    rows: 5,
    showColumns: true,
    showHeaderControls: false,
    showSearchField: false,
  }));

  public readonly creatorColumnTemplate = viewChild.required<TemplateRef<unknown>>('creatorColumn');
  public readonly campaignColumnTemplate = viewChild.required<TemplateRef<unknown>>('campaignColumn');
  public readonly dateColumnTemplate = viewChild.required<TemplateRef<unknown>>('dateColumn');
  public readonly deliverablesCountColumnTemplate = viewChild.required<TemplateRef<unknown>>('deliverablesCountColumn');
  public readonly groupedChildIndicatorColumnTemplate =
    viewChild.required<TemplateRef<unknown>>('groupedChildIndicatorColumn');
  public readonly groupedDeliverableGroupColumnTemplate = viewChild.required<TemplateRef<unknown>>(
    'groupedDeliverableGroupColumn',
  );
  public readonly groupedDeliverableColumnTemplate =
    viewChild.required<TemplateRef<unknown>>('groupedDeliverableColumn');
  public readonly statusColumnTemplate = viewChild.required<TemplateRef<unknown>>('statusColumn');
  public readonly deliverableGroupColumnTemplate = viewChild.required<TemplateRef<unknown>>('deliverableGroupColumn');
  public readonly deliverableColumnTemplate = viewChild.required<TemplateRef<unknown>>('deliverableColumn');
  public readonly matchingStatusColumnTemplate = viewChild.required<TemplateRef<unknown>>('matchingStatusColumn');

  public readonly columns = computed<TDatatableColumn<ICampaignDetailsDeliverablesTableRow, string>[]>(() => [
    {
      dataViewStrategy: (row): number => row.statusSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'status',
      isSortable: true,
    },
    {
      dataViewStrategy: (row): string => row.ctaTemplateName,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'deliverableGroup',
      isSortable: true,
    },
    {
      dataViewStrategy: (row): string => row.matchingTypeLabel,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'matching',
      isSortable: true,
    },
    {
      dataViewStrategy: (row): string => row.deliverableName,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'deliverable',
      isSortable: true,
    },
    this.contextColumn() === 'campaign'
      ? {
          dataViewStrategy: (row): string => row.campaignName ?? '',
          headerClasses: [EHeaderClasses.minWidth150],
          id: 'campaign',
          isSortable: true,
        }
      : {
          dataViewStrategy: (row): string => row.creatorName,
          headerClasses: [EHeaderClasses.minWidth150],
          id: 'creator',
          isSortable: true,
        },
    {
      dataViewStrategy: (row): number => row.dateSortValue,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'date',
      isSortable: true,
    },
  ]);

  public readonly configuration = computed<IDatatableConfiguration<ICampaignDetailsDeliverablesTableRow>>(() => ({
    defaultSort: { active: 'date', direction: 'desc' },
    isClientSide: true,
    isLoaderEnabled: true,
    isRowClickEnabled: this.campaignExportConfig().allowDeliverablesRowClick,
    isSearchEnabled: false,
    pageSizeOptions: [5, 10, 25, 50],
    skipInitialRequest: true,
    storageKey: `${this.storageKeyPrefix()}-deliverables-table`,
  }));

  public readonly customColumns = computed<ICustomColumn[]>(() => [
    { isSortable: true, name: 'status', template: this.statusColumnTemplate() },
    { isSortable: true, name: 'deliverableGroup', template: this.deliverableGroupColumnTemplate() },
    { isSortable: true, name: 'deliverable', template: this.deliverableColumnTemplate() },
    this.contextColumn() === 'campaign'
      ? { isSortable: true, name: 'campaign', template: this.campaignColumnTemplate() }
      : { isSortable: true, name: 'creator', template: this.creatorColumnTemplate() },
    { isSortable: true, name: 'date', template: this.dateColumnTemplate() },
    { isSortable: true, name: 'matching', template: this.matchingStatusColumnTemplate() },
  ]);

  public readonly groupedParentColumns = computed<
    IGroupedDatatableColumn<ICampaignDetailsDeliverablesGroupedParentRow>[]
  >(() => [
    this.contextColumn() === 'campaign'
      ? {
          id: 'campaign',
          isSearchable: true,
          isSortable: true,
          label: 'field.campaign',
          valueGetter: (row): string => row.campaignName ?? '',
        }
      : {
          id: 'creator',
          isSearchable: true,
          isSortable: true,
          label: 'field.creator',
          valueGetter: (row): string => row.creatorName,
        },
    {
      id: 'deliverableGroup',
      isSearchable: true,
      isSortable: true,
      label: 'field.deliverableGroup',
      valueGetter: (row): string => row.ctaTemplateName,
    },
    {
      id: 'date',
      isSearchable: true,
      isSortable: true,
      label: 'field.date',
      valueGetter: (row): number => row.dateSortValue,
    },
    {
      id: 'matching',
      isSearchable: true,
      isSortable: true,
      label: 'field.matching',
      valueGetter: (row): string => row.matchingTypeLabel,
    },
    {
      id: 'deliverables',
      isSearchable: false,
      isSortable: true,
      label: 'field.deliverables',
      valueGetter: (row): number => row.deliverablesCount,
    },
  ]);

  public readonly groupedChildColumns = computed<
    IGroupedDatatableColumn<ICampaignDetailsDeliverablesGroupedChildRow>[]
  >(() => [
    {
      id: 'childIndicator',
      isSearchable: false,
      isSortable: false,
      label: '',
      valueGetter: (): string => '',
    },
    {
      id: 'status',
      isSearchable: true,
      isSortable: false,
      label: 'field.status',
      valueGetter: (row): string => row.rowStatus,
    },
    {
      id: 'deliverable',
      isSearchable: true,
      isSortable: false,
      label: 'field.deliverable',
      valueGetter: (row): string => row.sourceRow.deliverableName,
    },
  ]);

  public readonly groupedParentCustomColumns = computed<
    IGroupedDatatableCustomColumn<ICampaignDetailsDeliverablesGroupedParentRow>[]
  >(() => [
    this.contextColumn() === 'campaign'
      ? { id: 'campaign', template: this.campaignColumnTemplate() }
      : { id: 'creator', template: this.creatorColumnTemplate() },
    { id: 'date', template: this.dateColumnTemplate() },
    { id: 'deliverableGroup', template: this.groupedDeliverableGroupColumnTemplate() },
    { id: 'deliverables', template: this.deliverablesCountColumnTemplate() },
    { id: 'matching', template: this.matchingStatusColumnTemplate() },
  ]);

  public readonly groupedChildCustomColumns = computed<
    IGroupedDatatableCustomColumn<ICampaignDetailsDeliverablesGroupedChildRow>[]
  >(() => [
    { id: 'childIndicator', template: this.groupedChildIndicatorColumnTemplate() },
    { id: 'deliverable', template: this.groupedDeliverableColumnTemplate() },
    { id: 'status', template: this.statusColumnTemplate() },
  ]);

  public readonly groupedConfiguration = computed<
    IGroupedDatatableConfiguration<
      ICampaignDetailsDeliverablesGroupedParentRow,
      ICampaignDetailsDeliverablesGroupedChildRow
    >
  >(() => ({
    childIndentPx: 0,
    defaultSort: { active: 'date', direction: 'desc' },
    hideParentsWithoutVisibleChildren: true,
    isChildHeaderEnabled: false,
    isChildRowClickEnabled: this.campaignExportConfig().allowDeliverablesRowClick,
    isPaginationEnabled: true,
    isParentRowClickEnabled: this.campaignExportConfig().allowDeliverableGroupClick,
    isSearchEnabled: false,
    pageSizeOptions: [5, 10, 25, 50],
    parentSortAccessor: (parent, sortKey): Date | number | string | null | undefined => {
      if (sortKey === 'campaign') {
        return parent.campaignName;
      }

      if (sortKey === 'creator') {
        return parent.creatorName;
      }

      if (sortKey === 'deliverableGroup') {
        return parent.ctaTemplateName;
      }

      if (sortKey === 'deliverables') {
        return parent.deliverablesCount;
      }

      if (sortKey === 'matching') {
        return parent.matchingTypeLabel;
      }

      if (sortKey === 'date') {
        const paddedDate = String(parent.dateSortValue).padStart(16, '0');
        const paddedPlannedOrder = String(parent.plannedUndesignatedOrder ?? 0).padStart(6, '0');

        return `${paddedDate}_${paddedPlannedOrder}_${parent.executionGroupingKey}`;
      }

      return '';
    },
    preserveExpandStateOnFilter: true,
    rowSeparatorEnabled: true,
    showExpandAllToggle: true,
    storageKey: `${this.storageKeyPrefix()}-deliverables-grouped-table`,
  }));

  public onGroupedChildRowClicked(row: ICampaignDetailsDeliverablesGroupedChildRow): void {
    if (!this.campaignExportConfig().allowDeliverablesRowClick) {
      return;
    }

    this.rowClicked.emit(row.sourceRow);
  }

  public onGroupedDeliverableGroupClicked(
    event: MouseEvent,
    row: ICampaignDetailsDeliverablesGroupedParentRow | ICampaignDetailsDeliverablesTableRow,
  ): void {
    event.stopPropagation();

    if (!this.groupedMode() || !this.campaignExportConfig().allowDeliverableGroupClick) {
      return;
    }

    this.groupedRowGroupClicked.emit(row as ICampaignDetailsDeliverablesGroupedParentRow);
  }

  // Always clickable, unlike the deliverable-group button above - this column only ever renders
  // in creator-details' Deliverables tab (contextColumn === 'campaign'), which has no export/
  // report concept to gate against (see CreatorDetailsDeliverablesComponent's own doc-comment on
  // campaignExportConfig). stopPropagation still matters: this button sits inside a row that's
  // independently clickable (opens the CTA analytics dialog), and this click should only ever
  // navigate to the campaign, never also open that dialog underneath it.
  public onCampaignClicked(
    event: MouseEvent,
    row: ICampaignDetailsDeliverablesGroupedParentRow | ICampaignDetailsDeliverablesTableRow,
  ): void {
    event.stopPropagation();

    if (!row.campaignId) {
      return;
    }

    this.campaignClicked.emit(row);
  }
}
